import { AbilityService } from "@archipad-js/core/ability";
import { IllegalArgumentError } from "@archipad-js/core/error";
import { ProjectEntity } from "@archipad-js/archipad-model";
import { ProjectMemberEntity } from "@archipad-js/archipad-model";
import { updateEntityWithProperties, query as ormQuery } from "@archipad-js/core/orm";
import { LocalizationService } from "@archipad-js/core/translate";
import { ArchipadAbility } from "./archipad-ability.types";
import { isDate, isSameDay } from "date-fns";
import { resolveCostPerUnitSurface, resolveOverallBudget } from "@archipad-js/archipad-model";

export class ProjectService {
    constructor(
        private readonly abilityService: AbilityService<ArchipadAbility>,
        private readonly localizationService: LocalizationService,
    ) {}

    public listProjectMembers(project: ProjectEntity): readonly ProjectMemberEntity[] {
        const entityContext = project.getContext();
        const query = ormQuery(entityContext, "ProjectMember");
        query.predicateWithFormat("person != null");
        query.predicateWithFormat("project == {project}", { project });
        query.orderBy("order");

        const resultSet = query.execute();
        return resultSet.sortedEntities();
    }

    //#region Building program section

    public shouldShowUrbanismSection(project: Readonly<ProjectEntity>): boolean {
        if (!project) {
            return false;
        }
        if (project.language === "fr") {
            return true;
        }
        return false;
    }

    public isReadonlyBuildingProgramSection(): boolean {
        return this.abilityService.ability.cannot("update", "section_infos_building_program");
    }

    public isValidBuildingSectionFormGroupPayload(payload: ProjectBuildingProgramSectionPayload): boolean {
        if (!this.isValidUrbanismSectionPayload(payload)) {
            return false;
        }
        if (!this.isValidSurfaceAreaSectionPayload(payload)) {
            return false;
        }
        if (!this.isValidCostSectionPayload(payload)) {
            return false;
        }
        if (!this.isValidPlanningSectionPayload(payload)) {
            return false;
        }
        return true;
    }

    public applyBuildingSectionUpdate(project: ProjectEntity, payload: ProjectBuildingProgramSectionPayload): void {
        const isValidPayload = this.isValidBuildingSectionFormGroupPayload(payload);
        if (!isValidPayload) {
            throw new IllegalArgumentError("Invalid Building section payload");
        }
        updateEntityWithProperties(project, payload);
    }

    //#region Urbanism Section

    /**
     * Regex to match the following pattern:
     * PD (for demolition permit);
     * 3 digits indicate the department number (057 for Moselle);
     * 3 digits indicate the commune code;
     * 2 digits indicate the year of application (22 for 2022);
     * 1 letter subdivision code: information can only be obtained from the town hall;
     * 4 digits give the file reference number.
     * Example: PD57340165A0004
     */
    public demolitionPermitRegex = /PD\d{3}\d{3}\d{2}[A-Z]\d{4}$/;

    /**
     * Regex to match the following pattern:
     * Total length: The license number must contain exactly 15 characters.
     * The first two characters must be "PC".
     * Characters 3 to 5 must be numbers between 001 and 999.
     * Characters 6 to 8 must be numbers between 001 and 999
     * Characters 9 and 10 must be numbers
     * Character 11 must be a letter
     * Characters 12 to 15 must be digits
     * Example: PC57340146F0004
     */
    public buildingPermitRegex = /PC\d{3}\d{3}\d{2}[A-Z]\d{4}$/;

    public getTypeOfWorkValues(): { value: string; primary: string }[] {
        return [
            { value: "buildingWork", primary: this.localizationService.l("Build work") },
            { value: "reconstructionWork", primary: this.localizationService.l("Reconstruction work") },
            { value: "enlargementWork", primary: this.localizationService.l("Enlargement work") },
        ];
    }

    private isValidUrbanismSectionPayload(payload: ProjectUrbanismSectionPayload): boolean {
        const isInvalidDemolitionPermit =
            payload?.demolitionPermit && !this.demolitionPermitRegex.test(payload.demolitionPermit);
        if (payload?.demolitionPermit && isInvalidDemolitionPermit) {
            return false;
        }

        const isInvalidBuildingPermit =
            payload?.buildingPermit && !this.buildingPermitRegex.test(payload.buildingPermit);
        if (payload?.buildingPermit && isInvalidBuildingPermit) {
            return false;
        }

        const isInvalidTypeOfWork =
            payload?.typeOfWork &&
            !this.getTypeOfWorkValues().some((typeOfWork) => typeOfWork.value === payload.typeOfWork);
        if (payload?.typeOfWork && isInvalidTypeOfWork) {
            return false;
        }

        return true;
    }

    //#endregion

    //#region Surface area Section

    public getUnitOfMeasureValues(): {
        value: string;
        primary: string;
        default?: boolean;
        unitOfMeasure: { linear: string; square: string; cubic: string };
    }[] {
        return [
            {
                value: "metric",
                primary: this.localizationService.l("Metric"),
                default: true,
                unitOfMeasure: {
                    linear: "m",
                    square: "m²",
                    cubic: "m³",
                },
            },
            {
                value: "imperial",
                primary: this.localizationService.l("Imperial"),
                unitOfMeasure: {
                    linear: "ft",
                    square: "sq ft",
                    cubic: "cu ft",
                },
            },
        ];
    }

    private isValidSurfaceAreaSectionPayload(payload: ProjectSurfaceAreaSectionPayload): boolean {
        const isInvalidUnitOfMeasure =
            payload?.unitOfMeasure &&
            !this.getUnitOfMeasureValues().some((unitOfMeasure) => unitOfMeasure.value === payload.unitOfMeasure);
        if (payload?.unitOfMeasure && isInvalidUnitOfMeasure) {
            return false;
        }

        const isInvalidDemolishedBuildingsSurface =
            !Number.isFinite(payload?.demolishedBuildingsSurface) ||
            (payload?.demolishedBuildingsSurface && payload.demolishedBuildingsSurface < 0);
        if (payload?.demolishedBuildingsSurface != null && isInvalidDemolishedBuildingsSurface) {
            return false;
        }

        const isInvalidLandArea = !Number.isFinite(payload?.landArea) || (payload?.landArea && payload.landArea < 0);
        if (payload?.landArea != null && isInvalidLandArea) {
            return false;
        }

        const isInvalidFloorSurface =
            !Number.isFinite(payload?.floorSurface) || (payload?.floorSurface && payload.floorSurface < 0);
        if (payload?.floorSurface != null && isInvalidFloorSurface) {
            return false;
        }

        const isInvalidBuildingHeight =
            !Number.isFinite(payload?.buildingHeight) || (payload?.buildingHeight && payload.buildingHeight < 0);
        if (payload?.buildingHeight != null && isInvalidBuildingHeight) {
            return false;
        }

        return true;
    }

    //#endregion

    //#region Cost Section

    public getCurrencyValues(): { value: string; primary: string; default?: boolean }[] {
        return [
            { value: "AID", primary: "AUD" }, // Australian dollar
            { value: "BRL", primary: "BRL" }, // Brazilian real
            { value: "CAD", primary: "CAD" }, // Canadian dollar
            { value: "CHF", primary: "CHF" }, // Swiss franc
            { value: "EUR", primary: "EUR", default: true }, // Euro
            { value: "GBP", primary: "GBP" }, // Pound sterling
            { value: "INR", primary: "INR" }, // Indian rupee
            { value: "USD", primary: "USD" }, // Dollar
        ];
    }

    private isValidCostSectionPayload(payload: ProjectCostSectionPayload): boolean {
        const isInvalidCurrency =
            payload?.currency && !this.getCurrencyValues().some((currency) => currency.value === payload.currency);
        if (payload?.currency && isInvalidCurrency) {
            return false;
        }

        const isInvalidEstimatedDemolitionBudget = !Number.isFinite(payload?.estimatedDemolitionBudget);
        if (payload?.estimatedDemolitionBudget != null && isInvalidEstimatedDemolitionBudget) {
            return false;
        }

        const isInvalidEstimatedConstructionBudget = !Number.isFinite(payload?.estimatedConstructionBudget);
        if (payload?.estimatedConstructionBudget != null && isInvalidEstimatedConstructionBudget) {
            return false;
        }

        const isInvalidRealDemolitionBudget = !Number.isFinite(payload?.realDemolitionBudget);
        if (payload?.realDemolitionBudget != null && isInvalidRealDemolitionBudget) {
            return false;
        }

        const isInvalidRealConstructionBudget = !Number.isFinite(payload?.realConstructionBudget);
        if (payload?.realConstructionBudget != null && isInvalidRealConstructionBudget) {
            return false;
        }

        return true;
    }

    public resolveOverallBudget(demolitionBudget: number, constructionBudget: number): number | null {
        return resolveOverallBudget(demolitionBudget, constructionBudget);
    }

    public resolveCostPerUnitSurface(overallBudget: number, floorSurface: number): number | null {
        return resolveCostPerUnitSurface(overallBudget, floorSurface);
    }

    //#endregion

    //#region Planning Section

    private isValidPlanningSectionPayload(payload: ProjectPlanningSectionPayload): boolean {
        const isInvalidEstimatedStartDate = !isDate(payload?.estimatedStartDate);
        if (payload?.estimatedStartDate != null && isInvalidEstimatedStartDate) {
            return false;
        }
        const isInvalidEstimatedEndDate = !isDate(payload?.estimatedEndDate);
        if (payload?.estimatedEndDate != null && isInvalidEstimatedEndDate) {
            return false;
        }
        const isInvalidRealStartDate = !isDate(payload?.realStartDate);
        if (payload?.realStartDate != null && isInvalidRealStartDate) {
            return false;
        }
        const isInvalidRealEndDate = !isDate(payload?.realEndDate);
        if (payload?.realEndDate != null && isInvalidRealEndDate) {
            return false;
        }

        if (
            payload?.estimatedStartDate != null &&
            payload?.estimatedEndDate != null &&
            !isSameDay(payload?.estimatedStartDate, payload?.estimatedEndDate) &&
            payload?.estimatedStartDate > payload?.estimatedEndDate
        ) {
            return false;
        }
        if (
            payload?.realStartDate != null &&
            payload?.realEndDate != null &&
            !isSameDay(payload.estimatedStartDate!, payload.estimatedEndDate!) &&
            payload?.realStartDate > payload?.realEndDate
        ) {
            return false;
        }
        return true;
    }

    //#endregion

    //#endregion
}

export type ProjectBuildingProgramSectionPayload = ProjectUrbanismSectionPayload &
    ProjectSurfaceAreaSectionPayload &
    ProjectCostSectionPayload &
    ProjectPlanningSectionPayload;

export type ProjectUrbanismSectionPayload = Partial<
    Pick<ProjectEntity, "demolitionPermit" | "buildingPermit" | "typeOfWork" | "townHallAddress">
>;

export type ProjectSurfaceAreaSectionPayload = Partial<
    Pick<ProjectEntity, "unitOfMeasure" | "demolishedBuildingsSurface" | "landArea" | "floorSurface" | "buildingHeight">
>;

export type ProjectCostSectionPayload = Partial<
    Pick<
        ProjectEntity,
        | "currency"
        | "estimatedDemolitionBudget"
        | "estimatedConstructionBudget"
        | "realDemolitionBudget"
        | "realConstructionBudget"
    >
>;

export type ProjectPlanningSectionPayload = Partial<
    Pick<ProjectEntity, "estimatedStartDate" | "estimatedEndDate" | "realStartDate" | "realEndDate">
>;
